"use client";

import { GENERATION_HISTORY_QUERY_KEY } from "@saas/shared/history/types";
import { compressImage } from "@shared/lib/image-compression";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { type ChangeEvent, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Uploads images straight into My Assets. `uploadImageToR2Directly` already
 * records the upload, so the only extra work here is compressing and
 * invalidating the feed.
 */
export function useUploadAsset({
	onUploaded,
}: { onUploaded?: (urls: string[]) => void } = {}) {
	const t = useTranslations("assets");
	const queryClient = useQueryClient();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isUploading, setIsUploading] = useState(false);
	// Drops and pastes can start a batch while another is still running; count
	// them so the first one to finish doesn't end the spinner for the rest
	const inFlightRef = useRef(0);

	// Shared by the file input and by drag-and-drop / paste callers
	const uploadFiles = async (files: File[]) => {
		if (files.length === 0) {
			return;
		}

		inFlightRef.current += 1;
		setIsUploading(true);
		try {
			const { uploadImageToR2Directly } = await import(
				"@shared/lib/r2-direct-upload"
			);
			const urls = await Promise.all(
				files.map(async (file) => {
					const compressed = await compressImage(file, {
						maxSizeMB: 2,
						maxWidthOrHeight: 1280,
						fileType: "image/webp",
					});
					return uploadImageToR2Directly(compressed);
				}),
			);

			await queryClient.invalidateQueries({
				queryKey: GENERATION_HISTORY_QUERY_KEY,
			});
			onUploaded?.(urls);
		} catch (error) {
			console.error("Error uploading asset:", error);
			toast.error(t("errors.uploadFailed"));
		} finally {
			inFlightRef.current -= 1;
			if (inFlightRef.current === 0) {
				setIsUploading(false);
			}
		}
	};

	const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files ?? []);
		// Reset immediately so picking the same file twice still fires
		event.target.value = "";
		await uploadFiles(files);
	};

	return {
		isUploading,
		openFilePicker: () => inputRef.current?.click(),
		inputRef,
		handleChange,
		uploadFiles,
	};
}
